import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { DocumentMagnifier } from './icons';

export interface EmptyStateProps {
  title: string;
  description?: string;
  action?: ReactNode;
}

/**
 * Centered empty state (Figma 2871:54696 no-sources, 3142:28540 empty sources,
 * 237:42124 empty filter results). The 150x150 illustrations from Figma are an
 * open question with the designer — a neutral icon stands in until exported.
 *
 * DocumentMagnifier is a raw <svg fill="currentColor"> icon (not an SvgIcon),
 * and it hardcodes width/height="20" with no `size` prop of its own.
 * Wrapping it in MUI's <SvgIcon> would nest a second <svg> whose inner
 * hardcoded width/height wins over any outer sx — a bug already caught twice
 * on this branch. Instead we override the inner <svg>'s size via a `& svg`
 * descendant selector and set an explicit colour on the wrapping Box, which
 * cascades down to the icon's `fill="currentColor"` paths.
 */
export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        py: 8,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          color: '#DEDEDE',
          '& svg': { width: 64, height: 64 },
        }}
      >
        <DocumentMagnifier />
      </Box>
      <Typography variant="h5">{title}</Typography>
      {description && (
        <Typography fontSize={14} color="text.secondary" sx={{ maxWidth: 360 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
}
